import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, ShieldCheck, Star } from "lucide-react";

interface DirectoryEntry {
  id: string;
  company_name: string | null;
  full_name: string | null;
  trade_specialties: string[] | null;
  job_categories: string[] | null;
  location: string | null;
  average_rating: number | null;
  total_reviews: number | null;
  logo_url: string | null;
  service_radius: number | null;
}

export function ContractorDirectory() {
  const [entries, setEntries] = useState<DirectoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.rpc("get_member_contractor_directory");
      if (error) {
        console.error("Directory load failed:", error.message);
      }
      setEntries((data as DirectoryEntry[]) ?? []);
      setLoading(false);
    };
    load();
  }, []);

  const term = search.trim().toLowerCase();
  const filtered = entries.filter((entry) => {
    if (!term) return true;
    const haystack = [
      entry.company_name,
      entry.full_name,
      entry.location,
      ...(entry.trade_specialties ?? []),
      ...(entry.job_categories ?? []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  });

  return (
    <div className="space-y-4">
      <Input
        placeholder="Search by trade, company or town"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        aria-label="Search the contractor directory"
      />

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading directory…</p>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground text-sm">No contractors match your search yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((entry) => (
            <Card key={entry.id} className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  {entry.logo_url && (
                    <img
                      src={entry.logo_url}
                      alt={`${entry.company_name || entry.full_name} logo`}
                      className="h-8 w-8 rounded object-cover"
                      loading="lazy"
                    />
                  )}
                  <span className="truncate">
                    {entry.company_name || entry.full_name || "Verified tradesman"}
                  </span>
                  <ShieldCheck className="h-4 w-4 text-primary flex-shrink-0" />
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex flex-wrap gap-1">
                  {(entry.trade_specialties ?? []).slice(0, 4).map((specialty) => (
                    <Badge key={specialty} variant="secondary" className="text-xs">
                      {specialty}
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  {entry.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {entry.location}
                    </span>
                  )}
                  {entry.average_rating != null && (
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3 text-yellow-500" />
                      {Number(entry.average_rating).toFixed(2)} ({entry.total_reviews ?? 0})
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
